import React, { useEffect, useState } from "react";
import { Calendar, Loader, Upload, X, Check } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import { resolveTransactionFeedback } from "utils/transactionFeedback";

export default function RecievedPaymentForm({ onClose }) {
  const [transactionType, setTransactionType] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [receivedTransactionCatagory, setReceivedTransactionCatagory] =
    useState(null);
  const { addAlert } = useAlert();
  const transactionTypes = [
    "Client Advance",
    "Running Bill Payment",
    "Final Project Payment",
    "Retention Money",
    "Security Deposit",
  ];
  const formatAmountPreview = (value) => {
    const numericValue = Number(value);
    if (value === "" || Number.isNaN(numericValue)) {
      return "Rs. 0";
    }
    return `Rs. ${numericValue.toLocaleString("en-IN")}`;
  };

  const formatDateForDisplay = (date) => {
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  const formatDateForAPI = (date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          const purchaseCatagory = data.find(
            (cat) => cat.catagory_name === "Received",
          );
          if (purchaseCatagory) {
            setReceivedTransactionCatagory(purchaseCatagory.id);
          }
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImage(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handleSave = async () => {
    if (!transactionType || !amount || isSaveDisabled) return;

    setIsLoading(true);
    setIsSaveDisabled(true); // Disable save button immediately after click

    const site = localStorage.getItem("site");
    const formattedDate = formatDateForAPI(selectedDate);

    const formData = new FormData();
    formData.append("transaction_type", transactionType);
    formData.append("transaction_date", formattedDate);
    formData.append("amount", parseInt(amount));
    formData.append("transaction_method", paymentMethod);
    formData.append("comments", remarks);
    formData.append("transaction_catagory_id", receivedTransactionCatagory);
    formData.append("site", parseInt(site));
    if (image) {
      formData.append("ireceipt_image", image);
    }

    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/finance/create-general-transaction/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );
      const data = await response.json();
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Payment created successfully",
        errorMessage: "Failed to create payment",
      });

      if (feedback.success) {
        if (feedback.approvalRequired) {
          addAlert(feedback.message, feedback.alertType);
        }
        onClose();
      } else {
        addAlert(feedback.message, feedback.alertType);
        // Re-enable button if API call fails
        setIsSaveDisabled(false);
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      addAlert(error.message || "Failed to create payment", "error");
      // Re-enable button if API call fails
      setIsSaveDisabled(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNow = () => {
    setSelectedDate(new Date());
    setShowDatePicker(false);
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex flex-row items-center justify-between px-4 h-16 py-2 border-b border-gray-100 relative shrink-0">
        <div />
        <h2 className="text-xl font-medium text-[#232323] text-center">
          Received
        </h2>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#232323] mb-2">
                Purpose of Transaction
              </label>
              <input
                type="text"
                className="w-full p-2.5 text-sm bg-white rounded-lg border border-gray-200 mb-2"
                placeholder="Type"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                {transactionTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setTransactionType(type)}
                    className={`px-3 py-1.5 rounded-full text-xs ${
                      transactionType === type
                        ? "bg-purple-100 text-purple-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-6">
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <label className="block text-sm font-medium text-[#232323] mb-2">
                  Payment Date
                </label>
                <div className="flex gap-2">
                  <button
                    className={`px-3 py-1.5 rounded-full text-xs ${
                      !showDatePicker
                        ? "bg-purple-100 text-purple-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                    onClick={handleSetNow}
                  >
                    Now
                  </button>
                  <button
                    className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-2 ${
                      showDatePicker
                        ? "bg-purple-100 text-purple-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                    onClick={() => setShowDatePicker(!showDatePicker)}
                  >
                    Pick an older date
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>

                {showDatePicker && (
                  <div className="mt-3 p-2 border border-gray-200 rounded-lg">
                    <div className="text-xs text-gray-600 mb-2">
                      {formatDateForDisplay(selectedDate)}
                    </div>
                    <input
                      type="date"
                      className="w-full p-2 border border-gray-200 rounded-md"
                      value={formatDateForAPI(selectedDate)}
                      max={formatDateForAPI(new Date())}
                      onChange={(e) => {
                        if (e.target.value) {
                          setSelectedDate(new Date(e.target.value));
                        }
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#232323] mb-2">
                    ₹ Amount
                  </label>
                  <input
                    type="text"
                    className="w-full p-2.5 text-sm bg-white rounded-lg border border-gray-200"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || parseFloat(val) >= 0) {
                        setAmount(val);
                      }
                    }}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formatAmountPreview(amount)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#232323] mb-2">
                    Payment Method
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["Cash", "UPI/Bank Transfer", "Cheque"].map((method) => (
                      <button
                        key={method}
                        className={`px-3 py-1.5 rounded-full text-xs ${
                          paymentMethod === method
                            ? "bg-purple-100 text-purple-600"
                            : "bg-gray-100 text-gray-600"
                        }`}
                        onClick={() => setPaymentMethod(method)}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <label className="block text-sm font-medium text-[#232323] mb-2">
                  Remarks / Comments (Optional)
                </label>
                <input
                  type="text"
                  className="w-full p-2 text-sm bg-transparent border-b border-gray-300"
                  placeholder="Type"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>

              <div className="rounded-xl border border-gray-100 bg-white p-4">
                <label className="block text-sm font-medium text-[#232323] mb-2">
                  Payment Slip (Optional)
                </label>
                {!previewUrl && (
                  <div className="flex gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="received-payment-image-upload"
                      onChange={handleImageUpload}
                      disabled={isSaveDisabled}
                    />
                    <label
                      htmlFor="received-payment-image-upload"
                      className={`flex-1 p-2.5 text-sm bg-purple-50 text-purple-600 rounded-md text-center cursor-pointer ${
                        isSaveDisabled ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Upload Photo
                      </span>
                    </label>
                  </div>
                )}
                {previewUrl && (
                  <div className="mt-3">
                    <div className="relative w-full h-48 rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
                      <img
                        src={previewUrl}
                        alt="Receipt"
                        className="w-full h-full object-contain rounded-md"
                      />
                      {!isSaveDisabled && (
                        <button
                          onClick={removeImage}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-gray-100 flex flex-col items-center justify-center shrink-0 bg-white">
        <button
          className={`min-w-[240px] text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
            isLoading || isSaveDisabled || !transactionType || !amount
              ? "bg-[#3B0069] cursor-not-allowed"
              : "bg-[#3B0069] hover:bg-[#3B0069D9]"
          }`}
          onClick={handleSave}
          disabled={isLoading || isSaveDisabled || !transactionType || !amount}
          style={{
            opacity:
              isLoading || isSaveDisabled || !transactionType || !amount
                ? 0.4
                : 1,
          }}
        >
          {isLoading ? "Saving..." : isSaveDisabled ? "Saved" : "Save"}
          <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
            {isLoading ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Check size={18} />
            )}
          </span>
        </button>
      </div>
    </div>
  );
}
